import {
  Element,
  Prop,
  Component,
  h,
  Event,
  EventEmitter,
  Host,
  Method,
} from "@stencil/core";
import classNames from "classnames";
import { dialogProps } from "./dialog-model";
import { generateRandomNumber } from "@globals/random-number-helpers";

/**
 * @name    m-dialog
 * @type    molecule
 *
 * @param {string} dialogType                   Type of Attribute (success, error, warning, alert)
 * @param {string} headerTitle                  Put Header Text to Appear Header
 * @param {string} bodyTitle                    Put Body Text to Appear Body Title
 * @param {boolean} closeIcon                   Pass True if You Want to Appear Icon
 * @param {string} optionalButtonText           Put Text if You Want To Display Button
 * @param {string} confirmButtonText            Put Text if You Want To Display Button
 * @param {string} cancelButtonText             Put Text if You Want To Display Button
 * @param {boolean} withTopBorder               Pass True if You Want to Appear Top Line
 * @param {string} iconName                     Pass Icon Name and Icon Text Then The Icon Appear
 * @param {string} iconText                     Pass Icon Text and Icon Name Then The Icon Appear
 * @param {boolean} showDialog                  Shows dialog
 * @event confirmed                             Emitted after confirming dialog
 * @event canceled                              Emitted after canceling dialog
 * @event backgroundClicked                     Emitted after clicking outside dialog
 * @event optionalClicked                       Emitted after clicking on optional button
 * @event closeClicked                          Emitted after clicking on close dialog button
 * @description
 * representation of lists
 */
@Component({
  tag: "f-dialog",
  styleUrl: "dialog.scss",
})
export class Dialog {
  @Element() el: HTMLFDialogElement;
  @Prop() dialogType: dialogProps["dialogType"] = "info";
  @Prop() headerTitle: dialogProps["headerTitle"];
  @Prop() bodyTitle: dialogProps["bodyTitle"];
  @Prop() closeIcon: dialogProps["closeIcon"];
  @Prop() optionalButtonText: dialogProps["optionalButtonText"];
  @Prop() confirmButtonText: dialogProps["confirmButtonText"];
  @Prop() cancelButtonText: dialogProps["cancelButtonText"];
  @Prop() withTopBorder: dialogProps["withTopBorder"];
  @Prop() iconName: dialogProps["iconName"];
  @Prop() iconText: dialogProps["iconText"];
  // prettier-ignore
  @Prop({ mutable: true, reflect: true }) showDialog: dialogProps["showDialog"] = false;

  @Event() confirmed: EventEmitter;
  @Event() canceled: EventEmitter;
  @Event() optionalClicked: EventEmitter;
  @Event() backgroundClicked: EventEmitter;
  @Event() closeClicked: EventEmitter;

  private dialogTitleId = `frontend-kit-dialog-alert-dialog-${
    this.dialogType
  }-title-${generateRandomNumber()}`;

  @Method()
  async openDialog(): Promise<void> {
    this.showDialog = true;
  }

  @Method()
  async closeDialog(): Promise<void> {
    this.showDialog = false;
  }

  render() {
    const dialogClass = classNames("m-dialog", {
      [`m-dialog--${this.dialogType}`]: !!this.dialogType,
      "-show-dialog": this.showDialog,
    });
    return (
      <Host
        onConfirmed={this.closeDialog}
        onCanceled={this.closeDialog}
        onBackgroundClicked={this.closeDialog}
        onCloseClicked={this.closeDialog}
      >
        <div
          class={dialogClass}
          role="dialog"
          aria-labelledby={this.dialogTitleId}
          /*TT CUSTOM START - fixed BCP no background click emit*/
          onClick={this.backgroundClicked.emit}
          aria-hidden="true"
          // onKeyDown={this.backgroundClicked.emit}
          /*TT CUSTOM END - fixed BCP no background click emit*/
        >
          <div
            class="a-box -floating"
            /*TT CUSTOM START - fixed BCP no background click emit*/
            onClick={function (e) {
              e.stopPropagation();
            }}
            aria-hidden="true"
            /*TT CUSTOM END - fixed BCP no background click emit*/
          >
            {this.headerTitle && (
              <div>
                {this.withTopBorder && (
                  <div class={`m-dialog__remark --${this.dialogType}`}></div>
                )}
                <div class="m-dialog__header">
                  <div class="m-dialog__header-title">
                    {this.iconName && this.iconText && (
                      <f-icon
                        iconName={this.iconName}
                        titleText={this.iconText}
                        isUiIcon
                      />
                    )}
                    <div class="m-dialog__title">{this.headerTitle}</div>
                  </div>
                  {this.closeIcon && (
                    <f-button
                      variant="integrated"
                      additionalProps={{ "aria-label": "close dialog" }}
                    >
                      <f-icon
                        iconName="close"
                        title="Close Icon"
                        onClick={this.closeClicked.emit}
                      />
                    </f-button>
                  )}
                </div>
                <hr class="a-divider" />
              </div>
            )}
            <div class="m-dialog__content">
              {this.bodyTitle && (
                <div class="m-dialog__headline" id={this.dialogTitleId}>
                  {this.bodyTitle}
                </div>
              )}
              <div class="m-dialog__body">
                <slot></slot>
              </div>
              <div class="m-dialog__actions">
                {this.optionalButtonText && (
                  <f-button
                    variant="secondary"
                    onClick={this.optionalClicked.emit}
                    type="button"
                  >
                    {this.optionalButtonText}
                  </f-button>
                )}
                <div class="m-dialog__actions-group">
                  {this.confirmButtonText && (
                    <f-button
                      variant="primary"
                      onClick={this.confirmed.emit}
                      type="button"
                    >
                      {this.confirmButtonText}
                    </f-button>
                  )}
                  {this.cancelButtonText && (
                    <f-button
                      variant="secondary"
                      onClick={this.canceled.emit}
                      type="button"
                    >
                      {this.cancelButtonText}
                    </f-button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
